import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { CampaignsTable } from '../../Campaigns/CampaignsTable/CampaignsTable';
import { ReactComponent as EmptyState } from './svg/empty-state.svg';
import css from './AutomationIndex.module.css';
import { TemplateGoButton } from './TemplateIndex/TemplateGoButton';
import { AutomationTemplateContainer } from './TemplateIndex/AutomationTemplateContainer';
import { PlusOutlined } from '@ant-design/icons';
import { Pagination } from 'antd';

interface Props {
  className: string;
}

export const AutomationIndex: FunctionComponent<Props> = props => {
  const { className } = props;
  const botId = useSelector(activeChatbotIdSelector);

  const [busy, setBusy] = useState(false);

  const { campaigns: automations, pagination } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};

  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();

  const fetchPage = useCallback(
    async (page?: number) => {
      setBusy(true);
      try {
        await campaignsOverviewCRUDActions.retrieveAll({
          botId,
          kind: 'automation',
          automationStats: true,
          pagination: {
            page: page || pagination?.page || 1,
            perPage: 8,
          },
        });
      } finally {
        setBusy(false);
      }
    },
    [botId, campaignsOverviewCRUDActions],
  );

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  if (!automations) {
    return null;
  }
  return (
    <div className={className}>
      {!automations?.length && !busy && (
        <div className={css.empty}>
          <span className={css.emptyTitle}>
            Create automations to engage with your contacts
          </span>
          <EmptyState className={css.emptyImage} />
          <AutomationTemplateContainer.Provider>
            <TemplateGoButton
              label="Build an Automation"
              leftIcon={<PlusOutlined />}
              css={{ button: css.addAutomationButton }}
            />
          </AutomationTemplateContainer.Provider>
        </div>
      )}

      {!!automations?.length && !busy && (
        <>
          <CampaignsTable
            campaigns={automations}
            fetchCurrentActiveKind={fetchPage}
          />

          <div className={css.pagination}>
            <Pagination
              current={pagination?.page}
              onChange={fetchPage}
              pageSize={pagination?.perPage}
              showSizeChanger={false}
              total={pagination?.total}
              disabled={busy}
              hideOnSinglePage
            />
          </div>
        </>
      )}
    </div>
  );
};
